import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import medallionsImage from "assets/enhancements.svg";
import LargeWeakenBar from "components/base/LargeWeakenBar";
import Medallions from "components/base/Medallions";
import Tooltip, {
  TooltipDesc,
  TooltipInfo,
  TooltipName,
  TooltipValues,
  ValuesIcon,
} from "components/base/Tooltip";
import { media } from "components/styles/breakpoints";
import { BASE_STATS_INFO } from "data/baseStats";
import { DERIVED_STATS, DerivedStatInfo } from "data/derivedStats";
import {
  getHighestInstalledUpgradeStatReq,
  getInstalledUpgradesByStatReq,
  getShipUpgradesData,
} from "libs/item";
import { getShipData, MAX_BASE_STAT_VALUE } from "libs/stats";
import {
  buyEnhanceStat,
  buyReduceStat,
  buyResetStats,
  showMessage,
} from "redux/actions";
import {
  getBuildInfo,
  getEnhancedBaseStats,
  getInstalledUpgrades,
  getMedallions,
  getTotalBaseStats,
} from "redux/selectors/characterSelectors";
import { CharacterBaseStats, CharacterDerivedStats } from "types";
import { COLORS } from "utils/constants";
import Button from "./Button";
import { ScreenButtonWrapper, screenItemMixin } from "./DataScreenBase";

interface BaseStatsProps {
  atShipworks?: boolean;
  onSetHighlightStat?: (x: keyof CharacterBaseStats) => void;
  onResetHighlightStat?: () => void;
}

interface IconProps {
  image: string;
}

const BaseStatsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const BaseStatsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EnhancementsIcon = styled.div<IconProps>`
  width: 12px;
  height: 12px;
  mask: url(${(props) => props.image}) no-repeat center;
  background-color: ${COLORS.ENHANCEMENTS};

  ${media.desktop`
    width: 16px;
    height: 16px;
  `}
`;

const EnhancementCostWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  transition: all 100ms ease-out;
  color: ${COLORS.ENHANCEMENTS};

  ${media.desktop`
    font-size: 18px;
  `}
`;

const EnhancementCost = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: 5px;
`;

const BaseStat = styled.div`
  ${screenItemMixin}
  cursor: pointer;
  padding-bottom: 15px;
`;

const BaseStatIcon = styled.div<IconProps>`
  height: 20px;
  width: 20px;
  background-color: ${(props) => props.color};
  mask: url(${(props) => props.image}) no-repeat center;
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const StatInfo = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const StatName = styled.div`
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;

  ${media.desktop`
    font-size: 18px;
  `}
`;

const BarWrapper = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

export default function BaseStats({
  atShipworks = false,
  onSetHighlightStat,
  onResetHighlightStat,
}: BaseStatsProps) {
  const dispatch = useDispatch();

  const { ship } = useSelector(getBuildInfo);
  const medallions = useSelector(getMedallions);
  const enhancedBaseStats = useSelector(getEnhancedBaseStats);
  const totalBaseStats = useSelector(getTotalBaseStats);
  const installedUpgrades = useSelector(getInstalledUpgrades);

  const statsCosts = getShipData(ship).baseStatsCosts;
  const shipUpgrades = getShipUpgradesData(installedUpgrades);

  // Use spent medallions instead of reset cost to enable/disable reset button, since mechanical engineers reset for free
  const canReset = !!medallions.statsSpent;

  const enhanceStat = (stat: keyof CharacterBaseStats) => {
    if (!!atShipworks) {
      dispatch(buyEnhanceStat({ baseStat: stat }));
    } else {
      dispatch(
        showMessage(`Find the nearest Loadout Hub to enhance this attribute`)
      );
    }
  };

  const reduceStat = (stat: keyof CharacterBaseStats) => {
    if (!!atShipworks) {
      dispatch(buyReduceStat({ baseStat: stat }));
    } else {
      dispatch(
        showMessage(`Find the nearest Loadout Hub to reduce this attribute`)
      );
    }
  };

  return (
    <BaseStatsWrapper>
      <Medallions />
      <BaseStatsList>
        {Object.values(BASE_STATS_INFO).map((baseStatInfo) => {
          const statSlug = baseStatInfo.slug as keyof CharacterBaseStats;
          const statCost = statsCosts[statSlug];
          const canIncreaseStat =
            statCost <= medallions.available &&
            totalBaseStats[statSlug] < MAX_BASE_STAT_VALUE;
          const canReduceStat = enhancedBaseStats[statSlug] > 0;
          const statReqInstalledUpgrades = getInstalledUpgradesByStatReq(
            statSlug,
            shipUpgrades
          );
          const upgradeNumber = getHighestInstalledUpgradeStatReq(
            statSlug,
            shipUpgrades
          );
          const atUpgradeStatReq = upgradeNumber === totalBaseStats[statSlug];

          return (
            <BaseStat
              key={statSlug}
              onMouseEnter={() => onSetHighlightStat?.(statSlug)}
              onMouseLeave={() => onResetHighlightStat?.()}
            >
              <Tooltip
                color={baseStatInfo.color}
                content={
                  <>
                    <TooltipName>{baseStatInfo.name}</TooltipName>
                    <TooltipDesc>{baseStatInfo.description}</TooltipDesc>
                    <TooltipInfo>
                      Each {baseStatInfo.name} increase adds:
                      {Object.entries(DERIVED_STATS)
                        .filter(([key, value]) => {
                          const statInfo = value as DerivedStatInfo;
                          return statInfo.baseStat === baseStatInfo.slug;
                        })
                        .map(([key, value]) => {
                          const statSlug = key as keyof CharacterDerivedStats;
                          const statInfo = value as DerivedStatInfo;

                          return (
                            <TooltipValues
                              key={statSlug}
                              color={statInfo.color}
                            >
                              <ValuesIcon
                                image={statInfo.image}
                                color={statInfo.color}
                              />
                              {`${statInfo.name}: ${statInfo.addFormatter(
                                statInfo.incrementValue
                              )}`}
                            </TooltipValues>
                          );
                        })}
                    </TooltipInfo>
                    {statReqInstalledUpgrades.length > 0 && (
                      <TooltipInfo>
                        Installed upgrades requiring {baseStatInfo.name}:
                        {statReqInstalledUpgrades.map((upgrade) => (
                          <TooltipValues
                            key={upgrade.slug}
                            color={baseStatInfo.color}
                          >
                            <ValuesIcon
                              image={baseStatInfo.image}
                              color={baseStatInfo.color}
                            />
                            {upgrade.name}: {upgrade.requirements[statSlug]}
                          </TooltipValues>
                        ))}
                      </TooltipInfo>
                    )}
                    <TooltipInfo>
                      <TooltipValues color={COLORS.ENHANCEMENTS}>
                        <ValuesIcon
                          image={medallionsImage}
                          color={COLORS.ENHANCEMENTS}
                        />
                        Medallion Cost: {statCost}
                      </TooltipValues>
                    </TooltipInfo>
                  </>
                }
              >
                <StatHeader>
                  <StatInfo>
                    <StatName>{baseStatInfo.name}</StatName>
                    <EnhancementCostWrapper>
                      <EnhancementCost>
                        <EnhancementsIcon image={medallionsImage} />
                        {statCost}
                      </EnhancementCost>
                    </EnhancementCostWrapper>
                  </StatInfo>
                  {!!atShipworks && (
                    <ActionsWrapper>
                      <Button
                        $style={atUpgradeStatReq ? "important" : "normal"}
                        $size="xsmall"
                        $disabled={!canReduceStat || !atShipworks}
                        onClick={() => reduceStat(statSlug)}
                      >
                        ⇓
                      </Button>
                      <Button
                        $style="normal"
                        $size="xsmall"
                        $disabled={!canIncreaseStat || !atShipworks}
                        onClick={() => enhanceStat(statSlug)}
                      >
                        ⇑
                      </Button>
                    </ActionsWrapper>
                  )}
                </StatHeader>
                <BarWrapper>
                  <BaseStatIcon
                    image={baseStatInfo.image}
                    color={baseStatInfo.color}
                  />
                  <LargeWeakenBar
                    number={totalBaseStats[statSlug]}
                    upgradeNumber={upgradeNumber}
                    maxNumber={MAX_BASE_STAT_VALUE}
                    color={baseStatInfo.color}
                    isReversed={false}
                  />
                </BarWrapper>
              </Tooltip>
            </BaseStat>
          );
        })}
      </BaseStatsList>
      {!!atShipworks && (
        <ScreenButtonWrapper>
          <Tooltip
            color={COLORS.IMPORTANT}
            content={
              <>
                <TooltipName>Reset Stats</TooltipName>
                <TooltipDesc>
                  Reset your base stats back to their default values and get
                  back all your spent stat medallions.
                </TooltipDesc>
                <TooltipDesc>
                  Any installed upgrades with requirements that your ship's
                  default stats no longer meet will be uninstalled.
                </TooltipDesc>
              </>
            }
          >
            <Button
              $style="important"
              $size="medium"
              $disabled={!canReset}
              onClick={() => dispatch(buyResetStats())}
            >
              Reset Stats
            </Button>
          </Tooltip>
        </ScreenButtonWrapper>
      )}
    </BaseStatsWrapper>
  );
}
